import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  FormControlLabel,
  Slider,
  Divider,
  Alert
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Percent as PercentIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Save as SaveIcon
} from '@mui/icons-material';

export default function PlatformSettings({ maintenanceMode, setMaintenanceMode }) {
  const [settings, setSettings] = useState({
    platformFee: 10, // Percentage
    minCampaignBudget: 50,
    maxCampaignBudget: 10000,
    minClipDuration: 15, // seconds
    maxClipDuration: 180, // seconds
    autoApprovalEnabled: false,
    emailNotificationsEnabled: true,
    maxFileSize: 100, // MB
    allowedFormats: ['mp4', 'mov', 'avi'],
    payoutSchedule: 'weekly',
    minimumPayout: 25
  });

  const [saved, setSaved] = useState(false);

  const handleSettingChange = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleSave = () => {
    // Save settings to Firebase
    console.log('Saving settings:', settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Platform Settings
      </Typography>

      {saved && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Financial Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
              <MoneyIcon sx={{ mr: 1 }} />
              Financial Settings
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Typography gutterBottom>Platform Fee (%)</Typography>
              <Slider
                value={settings.platformFee}
                onChange={(e, value) => handleSettingChange('platformFee', value)}
                valueLabelDisplay="auto"
                step={0.5}
                marks
                min={0}
                max={20}
              />
              <Typography variant="caption" color="text.secondary">
                Current: {settings.platformFee}% per transaction
              </Typography>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Min Campaign Budget"
                  type="number"
                  value={settings.minCampaignBudget}
                  onChange={(e) => handleSettingChange('minCampaignBudget', e.target.value)}
                  InputProps={{ startAdornment: '$' }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Max Campaign Budget"
                  type="number"
                  value={settings.maxCampaignBudget}
                  onChange={(e) => handleSettingChange('maxCampaignBudget', e.target.value)}
                  InputProps={{ startAdornment: '$' }}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Minimum Payout Amount"
              type="number"
              value={settings.minimumPayout}
              onChange={(e) => handleSettingChange('minimumPayout', e.target.value)}
              InputProps={{ startAdornment: '$' }}
              sx={{ mt: 2 }}
            />

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Payout Schedule</InputLabel>
              <Select
                value={settings.payoutSchedule}
                label="Payout Schedule"
                onChange={(e) => handleSettingChange('payoutSchedule', e.target.value)}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>
          </Paper>
        </Grid>

        {/* Content Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
              <StorageIcon sx={{ mr: 1 }} />
              Content Settings
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Min Clip Duration (sec)"
                  type="number"
                  value={settings.minClipDuration}
                  onChange={(e) => handleSettingChange('minClipDuration', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Max Clip Duration (sec)"
                  type="number"
                  value={settings.maxClipDuration}
                  onChange={(e) => handleSettingChange('maxClipDuration', e.target.value)}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Max File Size (MB)"
              type="number"
              value={settings.maxFileSize}
              onChange={(e) => handleSettingChange('maxFileSize', e.target.value)}
              sx={{ mb: 2 }}
            />

            <Typography variant="body2" sx={{ mb: 1 }}>
              Allowed Formats
            </Typography>
            <TextField
              fullWidth
              value={settings.allowedFormats.join(', ')}
              onChange={(e) => handleSettingChange('allowedFormats', e.target.value.split(', '))}
              placeholder="mp4, mov, avi"
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.autoApprovalEnabled}
                  onChange={(e) => handleSettingChange('autoApprovalEnabled', e.target.checked)}
                />
              }
              label="Enable Auto-Approval"
            />
          </Paper>
        </Grid>

        {/* System Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
              <SecurityIcon sx={{ mr: 1 }} />
              System Settings
            </Typography>

            <List>
              <ListItem>
                <ListItemText primary="Maintenance Mode" secondary="Disable user access for maintenance" />
                <FormControlLabel
                  control={
                    <Switch
                      checked={maintenanceMode}
                      onChange={(e) => setMaintenanceMode(e.target.checked)}
                      color="warning"
                    />
                  }
                  label=""
                />
              </ListItem>
              
              <ListItem>
                <ListItemText primary="Email Notifications" secondary="System-wide email notifications" />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailNotificationsEnabled}
                      onChange={(e) => handleSettingChange('emailNotificationsEnabled', e.target.checked)}
                    />
                  }
                  label=""
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
              <NotificationsIcon sx={{ mr: 1 }} />
              Notification Settings
            </Typography>

            <Typography variant="body2" sx={{ mb: 2 }}>
              Configure automatic notification triggers
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText primary="New User Registration" />
                <Switch defaultChecked />
              </ListItem>
              <ListItem>
                <ListItemText primary="Campaign Created" />
                <Switch defaultChecked />
              </ListItem>
              <ListItem>
                <ListItemText primary="Submission Approved" />
                <Switch defaultChecked />
              </ListItem>
              <ListItem>
                <ListItemText primary="Payout Processed" />
                <Switch defaultChecked />
              </ListItem>
              <ListItem>
                <ListItemText primary="System Alerts" />
                <Switch defaultChecked />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          sx={{ 
            bgcolor: '#ff5252', 
            '&:hover': { bgcolor: '#ff3939' },
            px: 4,
            py: 1.5
          }}
        >
          Save All Settings
        </Button>
      </Box>
    </Box>
  );
}
